import { supabase } from './supabase';
import type {
  AdminMeetingDeletePreview,
  AdminMeetingDeleteResult,
  AdminMeetingRow,
  AdminMeetingState,
  AdminRoom,
  AdminRoomInput,
  AdminUserMeetingRow,
  AdminUserRow,
  AttendanceVerificationCreation,
  CheckInResult,
  CreateMeetingSeriesResult,
  DashboardMetrics,
  Department,
  DepartmentDeleteResult,
  DepartmentUsage,
  DirectoryUser,
  HostMeetingState,
  MeetingCardData,
  MeetingLocationType,
  MeetingEditState,
  MeetingPublic,
  NotificationItem,
  OperationalMeetingState,
  ProjectorState,
  PublicAttendanceVerification,
  RoomAvailability,
  RoomCalendarRoom,
  RoomBookingConflictDetail,
  SafeUserMeetingState,
  UserProfile,
  UserImportRow,
  UserMeetingState,
  UpdateMeetingSeriesResult,
} from '../types';
import { normalizeEmail, normalizeProfileText } from './utils';

function rpcError(error: { message?: string } | null, fallback: string): never {
  throw new Error(error?.message || fallback);
}

export async function getDepartments(): Promise<Department[]> {
  const { data, error } = await supabase
    .from('departments')
    .select('department_id,department_name,sort_order')
    .order('sort_order', { ascending: true });
  if (error) rpcError(error, 'Unable to load departments.');
  return (data ?? []) as Department[];
}

export async function getUserProfile(email: string): Promise<UserProfile | null> {
  const { data, error } = await supabase.rpc('get_user_profile', { p_email: email });
  if (error) rpcError(error, 'Unable to look up user profile.');
  const row = Array.isArray(data) ? data[0] : data;
  return row ? (row as UserProfile) : null;
}

export async function guestProfileExists(email: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('guest_profile_exists', { p_email: email });
  if (error) rpcError(error, 'Unable to check guest registration.');
  return data === true;
}

export async function searchUserDirectory(query: string): Promise<DirectoryUser[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  const { data, error } = await supabase.rpc('search_user_directory', { p_query: q });
  if (error) rpcError(error, 'Unable to search existing users.');
  return (data ?? []) as DirectoryUser[];
}

export async function findUserDirectoryUser(email: string): Promise<DirectoryUser | null> {
  const normalizedEmail = normalizeEmail(email);
  const matches = await searchUserDirectory(normalizedEmail);
  return matches.find((item) => normalizeEmail(item.email) === normalizedEmail) ?? null;
}

export async function registerUser(input: {
  email: string;
  fullName: string;
  departmentOrg: string;
}): Promise<void> {
  const { data, error } = await supabase.rpc('register_user', {
    p_email: input.email,
    p_full_name: normalizeProfileText(input.fullName),
    p_department_org: normalizeProfileText(input.departmentOrg),
  });
  if (error) rpcError(error, 'Unable to register user.');
  if (data !== true) throw new Error('Registration could not be established.');
}

export async function getDashboardMetrics(email: string): Promise<DashboardMetrics> {
  const { data, error } = await supabase.rpc('dashboard_metrics', { p_email: email });
  if (error) rpcError(error, 'Unable to load dashboard metrics.');
  const row = Array.isArray(data) ? data[0] : data;
  return (row ?? { scheduled_meetings: 0, total_hosted: 0, total_checked_in: 0 }) as DashboardMetrics;
}

export async function listUserMeetings(email: string): Promise<MeetingCardData[]> {
  const { data, error } = await supabase.rpc('list_user_meetings', { p_email: email });
  if (error) rpcError(error, 'Unable to load your meetings.');
  return (data ?? []) as MeetingCardData[];
}


export async function getUserMeetingState(meetingId: string, email: string): Promise<UserMeetingState> {
  const { data, error } = await supabase.rpc('get_user_meeting_state', {
    p_meeting_id: meetingId,
    p_email: email,
  });
  if (error) {
    if (error.message?.includes('MEETING_ACCESS_DENIED')) {
      throw new Error('This meeting is not linked to your profile.');
    }
    rpcError(error, 'Unable to load meeting details.');
  }
  if (!data) throw new Error('Meeting not found or access denied.');
  return data as UserMeetingState;
}

export async function getMeetingPublic(meetingId: string): Promise<MeetingPublic> {
  const { data, error } = await supabase.rpc('get_meeting_public', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to load meeting.');
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Meeting not found.');
  return row as MeetingPublic;
}

export async function resolveMeetingJoinCode(code: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('resolve_meeting_join_code', { p_code: code });
  if (error) rpcError(error, 'Unable to resolve meeting code.');
  const row = Array.isArray(data) ? data[0] : data;
  return typeof row?.meeting_id === 'string' ? row.meeting_id : null;
}

export async function createMeeting(input: {
  title: string;
  date: string;
  time: string;
  location: string;
  chairman: string;
  hostEmail: string;
  expected: string[];
  hostSecret: string;
  projectorSecret: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('create_meeting', {
    p_title: input.title,
    p_date: input.date,
    p_time: input.time,
    p_location: input.location || null,
    p_chairman: input.chairman || null,
    p_host_email: input.hostEmail,
    p_expected: input.expected,
    p_host_secret: input.hostSecret,
    p_projector_secret: input.projectorSecret,
  });
  if (error) rpcError(error, 'Unable to create meeting.');
  const row = Array.isArray(data) ? data[0] : data;
  const id = row?.meeting_id ?? data;
  if (!id || typeof id !== 'string') throw new Error('Meeting was created but no meeting ID was returned.');
  return id;
}

export async function updateMeeting(input: {
  meetingId: string;
  hostSecret: string;
  title: string;
  date: string;
  time: string;
  location: string;
  chairman: string;
  expected: string[];
}): Promise<void> {
  const { error } = await supabase.rpc('update_meeting', {
    p_meeting_id: input.meetingId,
    p_host_secret: input.hostSecret,
    p_title: input.title,
    p_date: input.date,
    p_time: input.time,
    p_location: input.location || null,
    p_chairman: input.chairman || null,
    p_expected: input.expected,
  });
  if (error) {
    if (error.message?.includes('MEETING_CONTEXT_LOCKED_AFTER_ATTENDANCE')) {
      throw new Error('Date, time, location and chairman cannot be changed after attendance has been recorded.');
    }
    if (error.message?.includes('MEETING_DATE_IN_PAST')) {
      throw new Error('Meeting date cannot be moved into the past.');
    }
    if (error.message?.includes('MEETING_NOT_ACTIVE')) {
      throw new Error('Cancelled meetings cannot be edited.');
    }
    rpcError(error, 'Unable to update meeting.');
  }
}

export async function getHostMeetingState(meetingId: string, hostSecret: string): Promise<HostMeetingState> {
  const { data, error } = await supabase.rpc('get_host_meeting_state', {
    p_meeting_id: meetingId,
    p_host_secret: hostSecret,
  });
  if (error) rpcError(error, 'Unable to load host meeting state.');
  if (!data) throw new Error('Meeting not found or host access denied.');
  return data as HostMeetingState;
}

export async function getProjectorState(meetingId: string, projectorSecret: string): Promise<ProjectorState> {
  const { data, error } = await supabase.rpc('get_projector_state', {
    p_meeting_id: meetingId,
    p_projector_secret: projectorSecret,
  });
  if (error) rpcError(error, 'Unable to load projector state.');
  if (!data) throw new Error('Projector access denied.');
  return data as ProjectorState;
}

export async function checkIn(meetingId: string, email: string): Promise<CheckInResult> {
  const { data, error } = await supabase.rpc('check_in', {
    p_meeting_id: meetingId,
    p_user_email: email,
  });
  if (error) rpcError(error, 'Unable to check in.');
  return data as CheckInResult;
}

export async function manualCheckIn(input: {
  meetingId: string;
  hostSecret: string;
  email: string;
  fullName: string;
  departmentOrg: string;
}): Promise<CheckInResult> {
  const { data, error } = await supabase.rpc('manual_check_in', {
    p_meeting_id: input.meetingId,
    p_host_secret: input.hostSecret,
    p_user_email: input.email,
    p_full_name: input.fullName,
    p_department_org: input.departmentOrg,
  });
  if (error) rpcError(error, 'Unable to add manual attendance.');
  return data as CheckInResult;
}

export async function cancelMeeting(meetingId: string, hostSecret: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_meeting', {
    p_meeting_id: meetingId,
    p_host_secret: hostSecret,
  });
  if (error) rpcError(error, 'Unable to cancel meeting.');
}

export async function deleteMeetingIfEmpty(meetingId: string, hostSecret: string): Promise<void> {
  const { error } = await supabase.rpc('delete_meeting_if_empty', {
    p_meeting_id: meetingId,
    p_host_secret: hostSecret,
  });
  if (error) rpcError(error, 'Unable to delete meeting.');
}

export async function rotateProjectorSecret(meetingId: string, hostSecret: string, newSecret: string): Promise<void> {
  const { error } = await supabase.rpc('rotate_projector_secret', {
    p_meeting_id: meetingId,
    p_host_secret: hostSecret,
    p_new_projector_secret: newSecret,
  });
  if (error) rpcError(error, 'Unable to rotate projector access token.');
}

export async function adminListUsers(): Promise<AdminUserRow[]> {
  const { data, error } = await supabase.rpc('staff_admin_list_users_v18_3');
  if (error) rpcError(error, 'Unable to load users.');
  return (data ?? []) as AdminUserRow[];
}

export async function adminAddUser(row: UserImportRow): Promise<void> {
  const { error } = await supabase.rpc('staff_admin_add_user_v18_3', {
    p_email: row.email.trim().toLowerCase(),
    p_full_name: normalizeProfileText(row.full_name),
    p_department_id: row.department_id,
    p_department_org: row.department_org ? normalizeProfileText(row.department_org) : '',
    p_role: row.role,
  });
  if (error) {
    if (error.message?.includes('USER_ALREADY_EXISTS')) {
      throw new Error('A user with this email already exists. Edit the existing profile instead.');
    }
    rpcError(error, 'Unable to add user.');
  }
}

export async function adminUpsertUsers(rows: UserImportRow[]): Promise<number> {
  if (!rows.length) return 0;
  const payload = rows.map((row) => ({
    email: row.email.trim().toLowerCase(),
    full_name: normalizeProfileText(row.full_name),
    department_id: row.department_id,
    department_org: row.department_org ? normalizeProfileText(row.department_org) : '',
    role: row.role,
  }));
  const { data, error } = await supabase.rpc('staff_admin_upsert_users_v18_3', { p_rows: payload });
  if (error) rpcError(error, 'Unable to import users.');
  return Number(data ?? rows.length);
}

export async function adminUpdateUser(originalEmail: string, row: UserImportRow): Promise<void> {
  const { error } = await supabase.rpc('staff_admin_update_user_v18_3', {
    p_original_email: originalEmail,
    p_email: row.email.trim().toLowerCase(),
    p_full_name: normalizeProfileText(row.full_name),
    p_department_id: row.department_id,
    p_department_org: row.department_org ? normalizeProfileText(row.department_org) : '',
    p_role: row.role,
  });
  if (error) {
    if (error.message?.includes('ADMIN_EMAIL_IMMUTABLE')) throw new Error('Admin email cannot be changed because it must continue to match the Supabase Authentication account.');
    if (error.message?.includes('CANNOT_DEMOTE_CURRENT_ADMIN')) throw new Error('You cannot remove your own Admin role while signed in.');
    if (error.message?.includes('USER_ALREADY_EXISTS')) throw new Error('Another user already uses that email.');
    rpcError(error, 'Unable to update user.');
  }
}

export async function adminDeleteUser(email: string): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_user', { p_email: email });
  if (error) {
    if (error.message?.includes('CANNOT_DELETE_CURRENT_ADMIN')) throw new Error('You cannot delete the Admin account that is currently signed in.');
    rpcError(error, 'Unable to delete user.');
  }
}

export async function adminUserMeetings(email: string): Promise<AdminUserMeetingRow[]> {
  const { data, error } = await supabase.rpc('admin_user_meetings', { p_user_email: email });
  if (error) rpcError(error, 'Unable to load this user\'s meetings.');
  return (data ?? []) as AdminUserMeetingRow[];
}

export async function adminListDepartments(): Promise<Department[]> {
  const { data, error } = await supabase.rpc('admin_list_departments');
  if (error) rpcError(error, 'Unable to load departments.');
  return (data ?? []) as Department[];
}

export async function adminAddDepartment(name: string, sortOrder?: number): Promise<void> {
  const { error } = await supabase.rpc('admin_add_department', {
    p_department_name: name.trim(),
    p_sort_order: sortOrder ?? null,
  });
  if (error) rpcError(error, 'Unable to add department.');
}

export async function adminUpdateDepartment(department: Department): Promise<void> {
  const { error } = await supabase.rpc('admin_update_department', {
    p_department_id: department.department_id,
    p_department_name: department.department_name.trim(),
    p_sort_order: department.sort_order,
  });
  if (error) rpcError(error, 'Unable to update department.');
}

export async function staffAdminGetDepartmentUsage(departmentId: number): Promise<DepartmentUsage> {
  const { data, error } = await supabase.rpc('staff_admin_get_department_usage_v18_3', {
    p_department_id: departmentId,
  });
  if (error) rpcError(error, 'Unable to check current department usage.');
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Department usage was not returned.');
  return {
    department_id: Number(row.department_id),
    staff_count: Number(row.staff_count),
  };
}

export async function staffAdminDeleteDepartment(
  departmentId: number,
  replacementDepartmentId: number | null,
): Promise<DepartmentDeleteResult> {
  const { data, error } = await supabase.rpc('staff_admin_delete_department_v18_3', {
    p_department_id: departmentId,
    p_replacement_department_id: replacementDepartmentId,
  });
  if (error) {
    if (error.message?.includes('REPLACEMENT_DEPARTMENT_REQUIRED')) {
      throw new Error('Staff are now assigned to this department. Choose a replacement department and try again.');
    }
    if (error.message?.includes('REPLACEMENT_DEPARTMENT_NOT_FOUND')) {
      throw new Error('The replacement department is no longer available. Choose another department.');
    }
    if (error.message?.includes('REPLACEMENT_DEPARTMENT_MUST_DIFFER')) {
      throw new Error('Choose a replacement department different from the one being deleted.');
    }
    rpcError(error, 'Unable to move staff and delete the department.');
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Department deletion returned no result.');
  return {
    deleted_department_id: Number(row.deleted_department_id),
    moved_staff_count: Number(row.moved_staff_count),
    replacement_department_id: row.replacement_department_id === null
      ? null
      : Number(row.replacement_department_id),
  };
}

export async function adminListMeetings(filters?: {
  attendeeEmail?: string;
  hostEmail?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<AdminMeetingRow[]> {
  const { data, error } = await supabase.rpc('admin_list_meetings', {
    p_attendee_email: filters?.attendeeEmail || null,
    p_host_email: filters?.hostEmail || null,
    p_date_from: filters?.dateFrom || null,
    p_date_to: filters?.dateTo || null,
  });
  if (error) rpcError(error, 'Unable to load meetings.');
  return (data ?? []) as AdminMeetingRow[];
}

export async function adminGetMeetingState(meetingId: string): Promise<AdminMeetingState> {
  const { data, error } = await supabase.rpc('admin_get_meeting_state', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to load meeting details.');
  if (!data) throw new Error('Meeting not found.');
  return data as AdminMeetingState;
}

// v1.6 authenticated staff API. These RPCs derive the actor from auth.jwt();
// callers never pass an email as proof of staff identity.
export async function getMyStaffProfile(): Promise<UserProfile | null> {
  const { data, error } = await supabase.rpc('staff_get_my_profile_v18_3');
  if (error) rpcError(error, 'Unable to load your authenticated staff profile.');
  const row = Array.isArray(data) ? data[0] : data;
  return row ? row as UserProfile : null;
}

export async function onboardMyStaffProfile(input: { fullName: string; departmentId: number }): Promise<UserProfile> {
  const { data, error } = await supabase.rpc('staff_onboard_my_profile_v18_3', {
    p_full_name: normalizeProfileText(input.fullName),
    p_department_id: input.departmentId,
  });
  if (error) rpcError(error, 'Unable to create your staff profile.');
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Staff profile creation returned no profile.');
  return row as UserProfile;
}

export async function updateMyStaffProfile(input: { fullName: string; departmentId: number }): Promise<UserProfile> {
  const { data, error } = await supabase.rpc('staff_update_my_profile_v18_3', {
    p_full_name: normalizeProfileText(input.fullName),
    p_department_id: input.departmentId,
  });
  if (error) rpcError(error, 'Unable to update your staff profile.');
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Staff profile update returned no profile.');
  return row as UserProfile;
}

export async function getMyDashboardMetrics(): Promise<DashboardMetrics> {
  const { data, error } = await supabase.rpc('staff_dashboard_metrics');
  if (error) rpcError(error, 'Unable to load your dashboard metrics.');
  const row = Array.isArray(data) ? data[0] : data;
  return (row ?? { scheduled_meetings: 0, total_hosted: 0, total_checked_in: 0 }) as DashboardMetrics;
}

export async function listMyMeetings(): Promise<MeetingCardData[]> {
  const { data, error } = await supabase.rpc('staff_list_my_meetings_v19_2');
  if (error) rpcError(error, 'Unable to load your meetings.');
  return (data ?? []) as MeetingCardData[];
}

export async function listMyNotifications(limit = 50): Promise<NotificationItem[]> {
  const { data, error } = await supabase.rpc('staff_list_my_notifications_v19_2', { p_limit: limit });
  if (error) rpcError(error, 'Unable to load notifications.');
  return (data ?? []) as NotificationItem[];
}

export async function getUnreadNotificationCount(): Promise<number> {
  const { data, error } = await supabase.rpc('staff_get_unread_notification_count_v19_2');
  if (error) rpcError(error, 'Unable to load notification count.');
  return Number(data ?? 0);
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const { error } = await supabase.rpc('staff_mark_notification_read_v19_2', { p_notification_id: notificationId });
  if (error) rpcError(error, 'Unable to mark notification as read.');
}

export async function markAllNotificationsRead(): Promise<number> {
  const { data, error } = await supabase.rpc('staff_mark_all_notifications_read_v19_2');
  if (error) rpcError(error, 'Unable to mark notifications as read.');
  return Number(data ?? 0);
}

export async function getMyMeetingState(meetingId: string): Promise<SafeUserMeetingState> {
  const { data, error } = await supabase.rpc('staff_get_my_meeting_state_v19_3', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to load meeting details.');
  if (!data) throw new Error('Meeting not found or access denied.');
  return data as SafeUserMeetingState;
}

export async function getMyOperationalMeetingState(meetingId: string): Promise<OperationalMeetingState> {
  const { data, error } = await supabase.rpc('staff_get_operational_meeting_state_v19_1', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to load operational meeting details.');
  if (!data) throw new Error('Meeting not found or operational access denied.');
  return data as OperationalMeetingState;
}

export async function createAttendanceVerification(
  meetingId: string,
): Promise<AttendanceVerificationCreation> {
  const { data, error } = await supabase.rpc('staff_create_attendance_verification_v19_1', {
    p_meeting_id: meetingId,
    p_include_invalidated_attendance: false,
  });
  if (error) rpcError(error, 'Unable to prepare attendance verification.');
  if (!data) throw new Error('Attendance verification could not be prepared.');
  return data as AttendanceVerificationCreation;
}

export async function getPublicAttendanceVerification(
  token: string,
): Promise<PublicAttendanceVerification | null> {
  const { data, error } = await supabase.rpc('get_attendance_verification_v19_1', {
    p_token: token,
  });
  if (error) rpcError(error, 'Unable to verify attendance record.');
  return (data ?? null) as PublicAttendanceVerification | null;
}

export async function createMyMeeting(input: {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  locationType: MeetingLocationType;
  externalLocation: string;
  roomId: string | null;
  chairman: string;
  expected: string[];
  hostSecret: string;
  projectorSecret: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('staff_create_meeting_v18_1', {
    p_title: input.title,
    p_date: input.date,
    p_start_time: input.startTime,
    p_end_time: input.endTime,
    p_location_type: input.locationType,
    p_external_location: input.locationType === 'EXTERNAL' ? input.externalLocation || null : null,
    p_room_id: input.locationType === 'MTIB_ROOM' ? input.roomId : null,
    p_chairman: input.chairman || null,
    p_expected: input.expected,
    p_host_secret: input.hostSecret,
    p_projector_secret: input.projectorSecret,
  });
  if (error) rpcError(error, 'Unable to create meeting.');
  const row = Array.isArray(data) ? data[0] : data;
  const id = row?.meeting_id ?? data;
  if (!id || typeof id !== 'string') throw new Error('Meeting was created but no meeting ID was returned.');
  return id;
}

export async function listRoomAvailability(input: {
  date: string;
  startTime: string;
  endTime: string;
}): Promise<RoomAvailability[]> {
  const { data, error } = await supabase.rpc('staff_list_room_availability_v18_1', {
    p_date: input.date,
    p_start_time: input.startTime,
    p_end_time: input.endTime,
  });
  if (error) rpcError(error, 'Unable to load room availability.');
  return (data ?? []) as RoomAvailability[];
}

export async function getRoomCalendar(input: {
  startDate: string;
  dayCount?: number;
  roomId?: string | null;
}): Promise<RoomCalendarRoom[]> {
  const { data, error } = await supabase.rpc('staff_get_room_calendar_v19_2', {
    p_start_date: input.startDate,
    p_day_count: input.dayCount ?? 1,
    p_room_id: input.roomId ?? null,
  });
  if (error) rpcError(error, 'Unable to load the meeting room calendar.');
  return (data ?? []) as RoomCalendarRoom[];
}

export async function getRoomCalendarRange(input: {
  startDate: string;
  dayCount: number;
}): Promise<RoomCalendarRoom[]> {
  const { data, error } = await supabase.rpc('staff_get_room_calendar_range_v19_2', {
    p_start_date: input.startDate,
    p_day_count: input.dayCount,
  });
  if (error) rpcError(error, 'Unable to load room schedules.');
  return (data ?? []) as RoomCalendarRoom[];
}

export class RoomBookingConflictError extends Error {
  constructor(public readonly detail: RoomBookingConflictDetail) {
    super('Room no longer available.');
    this.name = 'RoomBookingConflictError';
  }
}

function roomConflictFromMessage(message: string | undefined): RoomBookingConflictDetail | null {
  if (!message) return null;
  const start = message.indexOf('{');
  const end = message.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  try {
    const detail = JSON.parse(message.slice(start, end + 1)) as Partial<RoomBookingConflictDetail>;
    if (detail.code === 'ROOM_ALREADY_BOOKED' && typeof detail.date === 'string'
      && typeof detail.room_id === 'string' && typeof detail.room_name === 'string'
      && typeof detail.start_time === 'string' && typeof detail.end_time === 'string') {
      return detail as RoomBookingConflictDetail;
    }
  } catch {
    // Fall through to the normal RPC error message.
  }
  return null;
}

export async function createMyMeetingSeries(input: {
  title: string;
  startDate: string;
  endDate: string;
  locationType: MeetingLocationType;
  externalLocation: string;
  chairman: string;
  expected: string[];
  cohostEmails?: string[];
  occurrences: Array<{
    date: string;
    startTime: string;
    endTime: string;
    hostSecret: string;
    projectorSecret: string;
    rooms: Array<{ roomId: string; startTime: string; endTime: string }>;
  }>;
}): Promise<CreateMeetingSeriesResult> {
  const { data, error } = await supabase.rpc('staff_create_meeting_series_v19', {
    p_payload: {
      title: input.title,
      starts_on: input.startDate,
      ends_on: input.endDate,
      location_type: input.locationType,
      external_location: input.locationType === 'EXTERNAL' ? input.externalLocation || null : null,
      chairman: input.chairman || null,
      expected: input.expected,
      cohost_emails: input.cohostEmails ?? [],
      occurrences: input.occurrences.map((occurrence) => ({
        date: occurrence.date,
        start_time: occurrence.startTime,
        end_time: occurrence.endTime,
        host_secret: occurrence.hostSecret,
        projector_secret: occurrence.projectorSecret,
        rooms: input.locationType === 'MTIB_ROOM' ? occurrence.rooms.map((room) => ({
          room_id: room.roomId,
          start_time: room.startTime,
          end_time: room.endTime,
        })) : [],
      })),
    },
  });
  if (error) {
    const conflict = roomConflictFromMessage(error.message);
    if (conflict) throw new RoomBookingConflictError(conflict);
    rpcError(error, 'Unable to create meeting series.');
  }
  const result = data as CreateMeetingSeriesResult | null;
  if (!result || !Array.isArray(result.occurrences) || !result.occurrences.length) {
    throw new Error('Meeting was created but no occurrence IDs were returned.');
  }
  return result;
}

export async function getMyHostedMeetingState(meetingId: string): Promise<HostMeetingState> {
  const { data, error } = await supabase.rpc('staff_get_host_meeting_state', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to load your host meeting state.');
  if (!data) throw new Error('Meeting not found or host access denied.');
  return data as HostMeetingState;
}

export async function updateMyHostedMeeting(input: {
  meetingId: string; title: string; date: string; time: string; location: string; chairman: string; expected: string[];
}): Promise<void> {
  const { error } = await supabase.rpc('staff_update_meeting', {
    p_meeting_id: input.meetingId, p_title: input.title, p_date: input.date, p_time: input.time,
    p_location: input.location || null, p_chairman: input.chairman || null, p_expected: input.expected,
  });
  if (error) rpcError(error, 'Unable to update meeting.');
}

export async function manualCheckInMyHostedMeeting(input: { meetingId: string; email: string; fullName: string; departmentId: number | null; organisation: string }): Promise<CheckInResult> {
  const { data, error } = await supabase.rpc('staff_manual_check_in_v19_1', {
    p_meeting_id: input.meetingId, p_user_email: input.email, p_full_name: input.fullName,
    p_department_id: input.departmentId, p_organisation: input.organisation || null,
  });
  if (error) rpcError(error, 'Unable to add manual attendance.');
  return data as CheckInResult;
}

export async function manualCheckInExpectedAttendee(input: {
  meetingId: string;
  expectedId: string;
  email: string;
  fullName: string;
  departmentId: number | null;
  organisation: string;
}): Promise<CheckInResult> {
  const { data, error } = await supabase.rpc('staff_manual_check_in_expected_v19_3', {
    p_meeting_id: input.meetingId,
    p_expected_id: input.expectedId,
    p_user_email: input.email,
    p_full_name: input.fullName,
    p_department_id: input.departmentId,
    p_organisation: input.organisation || null,
  });
  if (error) rpcError(error, 'Unable to complete Expected Attendee Check-in.');
  return data as CheckInResult;
}

export async function cancelMyHostedMeeting(meetingId: string, reasonCode = 'OTHER', reasonDetail: string | null = 'Cancelled by host', releaseRooms = true): Promise<void> {
  const { error } = await supabase.rpc('staff_cancel_meeting_v19_1', {
    p_meeting_id: meetingId, p_reason_code: reasonCode, p_reason_detail: reasonDetail || null, p_release_rooms: releaseRooms,
  });
  if (error) rpcError(error, 'Unable to cancel meeting.');
}

export async function setExpectedAttendeeDisposition(input: { expectedId: string; status: 'PENDING' | 'NOT_COMING'; reasonCode?: string | null; reasonDetail?: string | null }): Promise<void> {
  const { error } = await supabase.rpc('staff_set_expected_attendee_disposition_v19_1', {
    p_expected_id: input.expectedId,
    p_status: input.status,
    p_reason_code: input.reasonCode ?? null,
    p_reason_detail: input.reasonDetail ?? null,
  });
  if (error) rpcError(error, 'Unable to update attendee status.');
}

export async function removeExpectedAttendee(expectedId: string): Promise<void> {
  const { error } = await supabase.rpc('staff_remove_expected_attendee_v19_1', { p_expected_id: expectedId });
  if (error) rpcError(error, 'Unable to remove expected attendee.');
}

export async function removeMeetingCohost(input: { meetingId: string; email: string; removeExpected: boolean; scope?: 'OCCURRENCE' | 'SERIES' }): Promise<void> {
  const { error } = await supabase.rpc('staff_remove_meeting_cohost_v19_1', {
    p_meeting_id: input.meetingId,
    p_cohost_email: input.email,
    p_remove_expected: input.removeExpected,
    p_scope: input.scope ?? 'OCCURRENCE',
  });
  if (error) rpcError(error, 'Unable to remove co-host.');
}

export async function deleteMyHostedMeetingIfEmpty(meetingId: string): Promise<void> {
  const { error } = await supabase.rpc('staff_delete_meeting_if_empty', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to delete meeting.');
}

export async function rotateMyHostedProjectorSecret(meetingId: string, newSecret: string): Promise<void> {
  const { error } = await supabase.rpc('staff_rotate_projector_secret', { p_meeting_id: meetingId, p_new_projector_secret: newSecret });
  if (error) rpcError(error, 'Unable to rotate projector access token.');
}

export async function checkInAsAuthenticatedStaff(meetingId: string): Promise<CheckInResult> {
  const { data, error } = await supabase.rpc('staff_check_in', {
    p_meeting_id: meetingId,
  });
  if (error) rpcError(error, 'Unable to check in.');
  return data as CheckInResult;
}

export async function correctAttendanceAsStaff(input: { recordId: string; action: 'INVALIDATE' | 'RESTORE'; reason?: string; note?: string }): Promise<void> {
  const { error } = await supabase.rpc('staff_correct_attendance_v19', {
    p_attendance_record_id: input.recordId,
    p_action: input.action,
    p_reason: input.reason || null,
    p_note: input.note || null,
  });
  if (error) rpcError(error, 'Unable to correct attendance.');
}

export async function getProjectorStateV16(meetingId: string, projectorSecret: string): Promise<ProjectorState> {
  const { data, error } = await supabase.rpc('get_projector_state_v16', { p_meeting_id: meetingId, p_projector_secret: projectorSecret });
  if (error) rpcError(error, 'Unable to load projector state.');
  if (!data) throw new Error('Projector access denied.');
  return data as ProjectorState;
}

export async function getMyProjectorStateV19(meetingId: string): Promise<ProjectorState> {
  const { data, error } = await supabase.rpc('staff_get_projector_state_v19', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to load projector state.');
  if (!data) throw new Error('Projector access denied.');
  return data as ProjectorState;
}

export async function staffAdminListMeetings(filters?: { attendeeEmail?: string; hostEmail?: string; dateFrom?: string; dateTo?: string }): Promise<AdminMeetingRow[]> {
  const { data, error } = await supabase.rpc('staff_admin_list_meetings', {
    p_attendee_email: filters?.attendeeEmail || null,
    p_host_email: filters?.hostEmail || null,
    p_date_from: filters?.dateFrom || null,
    p_date_to: filters?.dateTo || null,
  });
  if (error) rpcError(error, 'Unable to load meetings.');
  return (data ?? []) as AdminMeetingRow[];
}

export async function staffAdminGetMeetingState(meetingId: string): Promise<AdminMeetingState> {
  const { data, error } = await supabase.rpc('staff_admin_get_meeting_state', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to load meeting details.');
  if (!data) throw new Error('Meeting not found.');
  return data as AdminMeetingState;
}

function adminPermanentDeleteError(error: { message?: string } | null, fallback: string): never {
  const message = error?.message ?? '';
  if (message.includes('ADMIN_ACCESS_DENIED') || message.includes('STAFF_AUTH_REQUIRED')) {
    throw new Error('Akses Admin Kehadiran diperlukan untuk tindakan ini.');
  }
  if (message.includes('MEETING_NOT_FOUND') || message.includes('Mesyuarat tidak ditemui')) {
    throw new Error('Mesyuarat tidak ditemui atau telah dipadam.');
  }
  if (message.includes('ROOM_RESERVATION_CROSS_MEETING_LINEAGE') || message.includes('rekod tempahan bilik yang berkaitan')) {
    throw new Error('Mesyuarat tidak boleh dipadam kerana terdapat rekod tempahan bilik yang berkaitan dengan mesyuarat lain.');
  }
  rpcError(error, fallback);
}

export async function adminPreviewMeetingDeleteV19_1(meetingId: string): Promise<AdminMeetingDeletePreview> {
  const { data, error } = await supabase.rpc('admin_preview_meeting_delete_v19_1', {
    p_meeting_id: meetingId,
  });
  if (error) adminPermanentDeleteError(error, 'Pratonton pemadaman mesyuarat tidak dapat dimuatkan.');
  if (!data) throw new Error('Pratonton pemadaman mesyuarat tidak dapat dimuatkan.');
  return data as AdminMeetingDeletePreview;
}

export async function adminDeleteMeetingPermanentlyV19_1(meetingId: string): Promise<AdminMeetingDeleteResult> {
  const { data, error } = await supabase.rpc('admin_delete_meeting_permanently_v19_1', {
    p_meeting_id: meetingId,
  });
  if (error) adminPermanentDeleteError(error, 'Mesyuarat tidak dapat dipadam secara kekal.');
  if (!data) throw new Error('Mesyuarat tidak dapat dipadam secara kekal.');
  return data as AdminMeetingDeleteResult;
}

export async function getMyMeetingEditState(meetingId: string): Promise<MeetingEditState> {
  const { data, error } = await supabase.rpc('staff_get_meeting_edit_state_v19', { p_meeting_id: meetingId });
  if (error) rpcError(error, 'Unable to load the meeting editor.');
  if (!data) throw new Error('Meeting not found or host access denied.');
  return data as MeetingEditState;
}

type MeetingEditPayload = {
  startsOn: string;
  endsOn: string;
  title: string;
  locationType: MeetingLocationType;
  externalLocation: string;
  chairman: string;
  expected: string[];
  cohostEmails?: string[];
  occurrences: Array<{
    meetingId?: string;
    date: string;
    startTime: string;
    endTime: string;
    status?: 'Active' | 'Cancelled';
    hostSecret?: string;
    projectorSecret?: string;
    rooms: Array<{ reservationId?: string; roomId: string; startTime: string; endTime: string }>;
  }>;
};

function meetingEditRpcError(error: { message?: string }, fallback: string): never {
  const conflict = roomConflictFromMessage(error.message);
  if (conflict) throw new RoomBookingConflictError(conflict);
  if (error.message?.includes('MEETING_CONTEXT_LOCKED_AFTER_ATTENDANCE')) {
    const dateMatch = /"date"\s*:\s*"([0-9-]+)"/.exec(error.message);
    throw new Error(`Structural meeting details are locked after attendance${dateMatch ? ` on ${dateMatch[1]}` : ''}.`);
  }
  if (error.message?.includes('SERIES_DATE_PREVIOUSLY_CANCELLED')) {
    throw new Error('A cancelled series day cannot be reactivated. Choose a different date range.');
  }
  if (error.message?.includes('MEETING_DATE_IN_PAST')) throw new Error('A meeting occurrence cannot be moved into the past.');
  rpcError(error, fallback);
}

function serializeMeetingEditPayload(input: MeetingEditPayload) {
  return {
    starts_on: input.startsOn,
    ends_on: input.endsOn,
    title: input.title,
    location_type: input.locationType,
    external_location: input.locationType === 'EXTERNAL' ? input.externalLocation || null : null,
    chairman: input.chairman || null,
    expected: input.expected,
    ...(input.cohostEmails ? { cohost_emails: input.cohostEmails } : {}),
    occurrences: input.occurrences.map((occurrence) => ({
      meeting_id: occurrence.meetingId ?? null,
      date: occurrence.date,
      start_time: occurrence.startTime,
      end_time: occurrence.endTime,
      status: occurrence.status ?? 'Active',
      host_secret: occurrence.hostSecret ?? null,
      projector_secret: occurrence.projectorSecret ?? null,
      rooms: input.locationType === 'MTIB_ROOM' ? occurrence.rooms.map((room) => ({
        reservation_id: room.reservationId ?? null,
        room_id: room.roomId,
        start_time: room.startTime,
        end_time: room.endTime,
      })) : [],
    })),
  };
}

export async function updateMyMeetingOccurrence(meetingId: string, input: Omit<MeetingEditPayload, 'occurrences'> & { occurrence: MeetingEditPayload['occurrences'][number] }): Promise<void> {
  const { error } = await supabase.rpc('staff_update_meeting_occurrence_v19', {
    p_meeting_id: meetingId,
    p_payload: serializeMeetingEditPayload({ ...input, occurrences: [input.occurrence] }),
  });
  if (error) meetingEditRpcError(error, 'Unable to update the meeting.');
}

export async function updateMyMeetingSeries(seriesId: string, input: MeetingEditPayload): Promise<UpdateMeetingSeriesResult> {
  const { data, error } = await supabase.rpc('staff_update_meeting_series_v19', {
    p_series_id: seriesId,
    p_payload: serializeMeetingEditPayload(input),
  });
  if (error) meetingEditRpcError(error, 'Unable to update the meeting series.');
  return data as UpdateMeetingSeriesResult;
}

export async function cancelMyHostedMeetingSeries(
  seriesId: string,
  selectedMeetingId: string,
  scope: 'ALL_ACTIVE' | 'FROM_SELECTED',
  reasonCode: string,
  reasonDetail: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('staff_cancel_meeting_series_v19_1', {
    p_series_id: seriesId,
    p_selected_meeting_id: selectedMeetingId,
    p_scope: scope,
    p_reason_code: reasonCode,
    p_reason_detail: reasonDetail,
  });
  if (error) rpcError(error, 'Unable to cancel the meeting series.');
}

export async function staffAdminListRooms(): Promise<AdminRoom[]> {
  const { data, error } = await supabase.rpc('staff_admin_list_rooms');
  if (error) rpcError(error, 'Unable to load rooms.');
  return (data ?? []) as AdminRoom[];
}

export async function staffAdminCreateRoom(input: AdminRoomInput): Promise<string> {
  const { data, error } = await supabase.rpc('staff_admin_create_room', {
    p_name: input.name,
    p_building: input.building,
    p_floor_label: input.floorLabel,
    p_floor_sort: input.floorSort,
    p_capacity: input.capacity,
    p_facilities: input.facilities,
    p_description: input.description,
    p_sort_order: input.sortOrder,
    p_active: input.active,
  });
  if (error) rpcError(error, 'Unable to create room.');
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.room_id || typeof row.room_id !== 'string') throw new Error('Room was created but no room ID was returned.');
  return row.room_id;
}

export async function staffAdminUpdateRoom(roomId: string, input: AdminRoomInput): Promise<void> {
  const { error } = await supabase.rpc('staff_admin_update_room', {
    p_room_id: roomId,
    p_name: input.name,
    p_building: input.building,
    p_floor_label: input.floorLabel,
    p_floor_sort: input.floorSort,
    p_capacity: input.capacity,
    p_facilities: input.facilities,
    p_description: input.description,
    p_sort_order: input.sortOrder,
  });
  if (error) rpcError(error, 'Unable to update room.');
}

export async function staffAdminSetRoomActive(roomId: string, active: boolean): Promise<void> {
  const { error } = await supabase.rpc('staff_admin_set_room_active', {
    p_room_id: roomId,
    p_active: active,
  });
  if (error) rpcError(error, `Unable to ${active ? 'activate' : 'deactivate'} room.`);
}
